import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../utils/api';
import { Activity, ArrowRight, Check, Wallet } from '../components/Icons';
import './OverviewScreen.css';
const EVENT_META = {
  'session.created':          { label: 'Session created',       color: 'purple' },
  'session.verified':         { label: 'Session verified',      color: 'green'  },
  'session.failed':           { label: 'Session failed',        color: 'red'    },
  'session.expired':          { label: 'Session expired',       color: 'red'    },
  'token.exchanged':          { label: 'Token exchanged',       color: 'purple' },
  'api_key.generated':        { label: 'API key generated',     color: 'grey'   },
  'branding.updated':         { label: 'Branding updated',      color: 'grey'   },
  'integration.connected':    { label: 'Integration connected', color: 'green'  },
  'integration.disconnected': { label: 'Integration removed',   color: 'orange' },
  'token.rejected':           { label: 'Token rejected',        color: 'red'    },
  'api_key.rotated':          { label: 'API keys rotated',      color: 'grey'   },
  'wallet.enabled':           { label: 'Wallet enabled',        color: 'green'  },
  'wallet.disabled':          { label: 'Wallet disabled',       color: 'orange' },
};

const BADGE_CLASS = { purple: 'vt-tag--brand', green: 'vt-tag--success', red: 'vt-tag--danger', orange: 'vt-tag--warn', grey: '' };

function EventBadge({ event }) {
  const meta = EVENT_META[event] || { label: event, color: 'grey' };
  return <span className={`vt-tag ${BADGE_CLASS[meta.color] || ''}`}>{meta.label}</span>;
}

function StatCard({ label, value, sub, loading }) {
  return (
    <div className="vt-panel vt-stat" style={{ marginTop: 0 }}>
      <div className="vt-statLabel">{label}</div>
      <div className="vt-statValue">
        {loading ? <span className="vt-ov-skeleton" /> : value}
      </div>
      {sub && <div className="vt-statSub">{sub}</div>}
    </div>
  );
}

function formatTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const now = new Date();
  const diffMs = now - d;
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1)  return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function walletLabel(id) {
  const map = {
    eudi:    'EUDI Wallet',
    eudi_de: 'EUDI (Germany)',
    eudi_ie: 'EUDI (Ireland)',
    eudi_fr: 'France Identité',
  };
  return map[id] || id;
}

export default function OverviewScreen({ firstName }) {
  const navigate = useNavigate();
  const name = firstName
    ? firstName.charAt(0).toUpperCase() + firstName.slice(1).toLowerCase()
    : 'there';

  const [stats, setStats]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasKeys, setHasKeys] = useState(null);
  const [enabledWallets, setEnabledWallets] = useState([]);

  useEffect(() => {
    Promise.all([
      api.getStats().catch(() => null),
      api.getApiKeys().catch(() => null),
      api.getVerificationConfig().catch(() => null),
    ]).then(([s, keys, cfg]) => {
      setStats(s);
      setHasKeys(!!(keys?.publicKey));
      setEnabledWallets(cfg?.enabled_wallets || []);
      setLoading(false);
    });
  }, []);

  const setupItems = [
    { label: 'Enable wallets in Wallet Hub',  done: enabledWallets.length > 0, path: '/wallet-hub' },
    { label: 'Generate your API keys',        done: hasKeys === true,          path: '/api-keys'   },
    { label: 'Make your first verification',  done: stats?.sessions_total > 0, path: null          },
  ];
  const allDone = setupItems.every(i => i.done);

  const doneCount = setupItems.filter(i => i.done).length;

  return (
    <div className="vt-ov">
      <div className="vt-pageHead vt-pageHeadRow">
        <div>
          <div className="vt-eyebrow">Dashboard</div>
          <h1 className="vt-pageTitle">
            Welcome back, <span className="vt-accent">{name}</span>
          </h1>
          <p className="vt-pageSub">Here's a summary of your identity verification activity.</p>
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <a className="vt-btn vt-btn--ghostPill vt-btn--lg" href="https://docs.veritage.io" target="_blank" rel="noreferrer">Docs</a>
          <a className="vt-btn vt-btn--primary vt-btn--lg" href="https://playground.veritage.io" target="_blank" rel="noreferrer">
            Try a verification <span className="vt-btnArrow"><ArrowRight size={13} /></span>
          </a>
        </div>
      </div>

      <div className="vt-grid4" style={{ marginBottom: 20 }}>
        <StatCard label="Sessions today" value={stats?.sessions_today ?? 0}     loading={loading} />
        <StatCard label="This week"      value={stats?.sessions_this_week ?? 0} loading={loading} />
        <StatCard label="All time"       value={stats?.sessions_total ?? 0}     loading={loading} />
        <StatCard label="Success rate"   value={stats?.success_rate ?? '—'} sub="Last 30 days" loading={loading} />
      </div>

      <div className="vt-ov-cols">
        <section className="vt-panel" style={{ marginTop: 0 }}>
          <div className="vt-panelHead">
            <div>
              <h2 className="vt-panelTitle">Recent activity</h2>
              <p className="vt-panelSub">The latest events on your account.</p>
            </div>
            <button type="button" className="vt-btn vt-btn--ghostPill vt-btn--sm" onClick={() => navigate('/audit-logs')}>View all</button>
          </div>

          {loading ? (
            <div className="vt-ov-activity-list">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="vt-ov-activity-row">
                  <span className="vt-ov-skeleton vt-ov-skeleton--badge" />
                  <span className="vt-ov-skeleton vt-ov-skeleton--time" />
                </div>
              ))}
            </div>
          ) : !stats?.recent_events?.length ? (
            <div className="vt-ov-emptyState">
              <span className="vt-tile"><Activity /></span>
              <div>
                <div className="vt-optionTitle">No events yet</div>
                <p className="vt-fieldHint" style={{ margin: '4px 0 0' }}>Run your first verification to see activity here.</p>
              </div>
            </div>
          ) : (
            <div className="vt-ov-activity-list">
              {stats.recent_events.map((ev, i) => (
                <div key={i} className="vt-ov-activity-row">
                  <EventBadge event={ev.event} />
                  <span className="vt-ov-activity-time">{formatTime(ev.created_at)}</span>
                </div>
              ))}
            </div>
          )}
        </section>

        <div className="vt-ov-right-col">
          {!allDone && (
            <section className="vt-panel" style={{ marginTop: 0 }}>
              <div className="vt-panelHead" style={{ marginBottom: 16 }}>
                <div>
                  <h2 className="vt-panelTitle">Get started</h2>
                  <p className="vt-panelSub">{doneCount} of {setupItems.length} done</p>
                </div>
                <span className="vt-tag vt-tag--brand">{doneCount}/{setupItems.length}</span>
              </div>
              <ol className="vt-steps">
                {setupItems.map((item, i) => (
                  <li key={i}>
                    <button
                      type="button"
                      className={['vt-step', item.done ? 'vt-step--done' : ''].filter(Boolean).join(' ')}
                      onClick={() => item.path && !item.done && navigate(item.path)}
                      disabled={!item.path || item.done}
                      style={{ cursor: item.path && !item.done ? 'pointer' : 'default' }}
                    >
                      <span className="vt-stepNum">{item.done ? <Check size={14} /> : i + 1}</span>
                      <span className="vt-stepText">{item.label}</span>
                      {!item.done && item.path && <span className="vt-stepArrow"><ArrowRight size={15} /></span>}
                    </button>
                  </li>
                ))}
              </ol>
            </section>
          )}

          <section className="vt-panel" style={{ marginTop: 0 }}>
            <div className="vt-panelHead" style={{ marginBottom: 16 }}>
              <div>
                <h2 className="vt-panelTitle">Active wallets</h2>
                <p className="vt-panelSub">Offered on your verification screen.</p>
              </div>
              <button type="button" className="vt-btn vt-btn--ghostPill vt-btn--sm" onClick={() => navigate('/wallet-hub')}>Configure</button>
            </div>
            {loading ? (
              <span className="vt-ov-skeleton" style={{ width: 80 }} />
            ) : enabledWallets.length === 0 ? (
              <p className="vt-fieldHint" style={{ margin: 0 }}>No wallets enabled yet.</p>
            ) : (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {enabledWallets.map(w => (
                  <span key={w} className="vt-chipCheck"><Wallet size={15} /> {walletLabel(w)}</span>
                ))}
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}
